import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Header, Icon, Modal, Radio, Image } from 'semantic-ui-react';
import AuthService from '../../../../service/auth';
import { ROLES } from '../../../../constnats/user';
import { AssetUtils } from '../../../../utils/assetsUtils';
import Alert from '../../../../utils/alert';
import logoSrc from '../../../logo/logo.svg';
import { DownloadConfig, ExportFormatValue, Resolution } from '../../types';
import { DEFAULT_DOWNLOAD_CONFIG } from '../../constants';
import {
    calculateLegendHeight,
    calculateScaledDimensions,
    compositeChartImage,
    getLogoReserveHeight,
    HEADING_HEIGHT_PX,
    renderEChartsToImage,
} from '../../chartImageUtils';
import {
    useChartStoreHeadingState,
    useEChartsRef,
    useEChartsOption,
    useLegendItems,
    useChartData,
    useChartCompanies,
} from '../../context';

type Props = {
    open: boolean;
    onClose: () => void;
};

function DownloadChartModal({ open, onClose }: Props) {
    const { chartReady } = useEChartsRef();
    const [downloadConfig, setDownloadConfig] = useState<DownloadConfig>(DEFAULT_DOWNLOAD_CONFIG);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const imageUrlRef = useRef<string | null>(null);

    const isEmployee = AuthService.hasAnyGlobalRole([ROLES.EMPLOYEE] as any);
    const companies = useChartCompanies();
    const companiesWithLogos = useMemo(
        () => companies.map((c) => ({
            companyName: c.companyName,
            companyLogoURL: AssetUtils.buildLogoURL(c.companyId),
        })),
        [companies],
    );

    const { heading } = useChartStoreHeadingState();
    const echartsOption = useEChartsOption();
    const legendItems = useLegendItems();
    const { hasChartData } = useChartData();

    const generateImage = useCallback(
        async (config: DownloadConfig): Promise<string | null> => {
            if (!hasChartData || !config.width || !config.height) {
                return null;
            }

            try {
                const { scaledWidth, scaledHeight } = calculateScaledDimensions(
                    config.width,
                    config.height,
                    config.resolution,
                );
                const headingSpace = config.showHeadings ? Math.round(HEADING_HEIGHT_PX * config.resolution) : 0;
                const legendSpace = calculateLegendHeight(legendItems.length, config.resolution);
                const logoSpace = await getLogoReserveHeight(logoSrc, config.resolution);
                const availableChartHeight = scaledHeight - headingSpace - legendSpace - logoSpace;

                const logicalWidth = config.width;
                const logicalChartHeight = Math.max(1, availableChartHeight / config.resolution);

                const chartImgUri = await renderEChartsToImage({
                    option: echartsOption,
                    logicalWidth,
                    logicalHeight: logicalChartHeight,
                    config,
                });

                const cacheBustedCompanies = companiesWithLogos.map((c) => ({
                    ...c,
                    companyLogoURL: c.companyLogoURL ? `${c.companyLogoURL}?t=${Date.now()}` : null,
                }));

                return await compositeChartImage({
                    chartImgUri,
                    config,
                    companies: cacheBustedCompanies,
                    heading,
                    isEmployee,
                    logoSrc,
                    scaledWidth,
                    scaledChartHeight: availableChartHeight,
                    legendItems,
                    logoReserveHeight: logoSpace,
                });
            } catch (err) {
                Alert.error('Chart export failed');
                return null;
            }
        },
        [hasChartData, legendItems, echartsOption, companiesWithLogos, heading, isEmployee],
    );

    const handleGeneratePreview = useCallback(async () => {
        const url = await generateImage(downloadConfig);
        if (url) {
            setImageUrl((prev) => {
                if (prev) {
                    URL.revokeObjectURL(prev);
                }
                imageUrlRef.current = url;
                return url;
            });
        }
    }, [generateImage, downloadConfig]);

    const handleDownloadChart = useCallback(async () => {
        const url = imageUrl ?? await generateImage(downloadConfig);
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = `${downloadConfig.fileName}.${downloadConfig.exportAs}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            if (url !== imageUrl) {
                URL.revokeObjectURL(url);
            }
            onClose();
        }
    }, [imageUrl, generateImage, downloadConfig, onClose]);

    // Preview debounce
    useEffect(() => {
        if (!open || !chartReady) {
            return;
        }
        const timer = setTimeout(() => {
            handleGeneratePreview();
        }, 300);
        return () => clearTimeout(timer);
    }, [open, downloadConfig, chartReady, handleGeneratePreview]);

    // Cleanup image URL on unmount
    useEffect(() => {
        return () => {
            if (imageUrlRef.current) {
                URL.revokeObjectURL(imageUrlRef.current);
            }
        };
    }, []);

    return (
        <Modal size="small" open={open} onClose={onClose}>
            <Modal.Header>Download Chart</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Group widths={'equal'}>
                        <Form.Input
                            placeholder="File Name"
                            label="File Name"
                            value={downloadConfig.fileName}
                            onChange={(_, data) => {
                                setDownloadConfig((prev) => ({ ...prev, fileName: data.value }));
                            }}
                        />
                        <Form.Input
                            placeholder="Width"
                            label="Width (px)"
                            type="number"
                            value={downloadConfig.width}
                            onChange={(_, data) => {
                                const width = parseInt(data.value, 10) || 0;
                                setDownloadConfig((prev) => ({ ...prev, width }));
                            }}
                            min="100"
                            max="4000"
                        />
                        <Form.Input
                            placeholder="Height"
                            label="Height (px)"
                            type="number"
                            value={downloadConfig.height}
                            onChange={(_, data) => {
                                const height = parseInt(data.value, 10) || 0;
                                setDownloadConfig((prev) => ({ ...prev, height }));
                            }}
                            min="100"
                            max="4000"
                        />
                        <div className="relative">
                            <Form.Input
                                icon={<Icon style={{ color: downloadConfig.backgroundColor }} name="circle thin" />}
                                iconPosition="left"
                                placeholder="Background Color"
                                label="Background Color"
                                type="text"
                                value={downloadConfig.backgroundColor}
                                onChange={(_, data) => {
                                    setDownloadConfig((prev) => ({ ...prev, backgroundColor: data.value }));
                                }}
                            />
                            <input
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                }}
                                type="color"
                                value={downloadConfig.backgroundColor}
                                onChange={(e) => {
                                    setDownloadConfig((prev) => ({ ...prev, backgroundColor: e.target.value }));
                                }}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group className="flex flex-align-center" widths={'equal'}>
                        <Form.Field>
                            <label>Resolution</label>
                            <Radio
                                label="1x"
                                value="1"
                                checked={downloadConfig.resolution === Resolution.LOW}
                                onChange={() => setDownloadConfig((prev) => ({ ...prev, resolution: Resolution.LOW }))}
                            />
                            <Radio
                                label="2x"
                                value="2"
                                checked={downloadConfig.resolution === Resolution.MEDIUM}
                                onChange={() => setDownloadConfig((prev) => ({ ...prev, resolution: Resolution.MEDIUM }))}
                            />
                            <Radio
                                label="3x"
                                value="3"
                                checked={downloadConfig.resolution === Resolution.HIGH}
                                onChange={() => setDownloadConfig((prev) => ({ ...prev, resolution: Resolution.HIGH }))}
                            />
                        </Form.Field>
                        <Form.Checkbox
                            label="Show Headings"
                            checked={downloadConfig.showHeadings}
                            onChange={(_, data) => {
                                setDownloadConfig((prev) => ({ ...prev, showHeadings: data.checked ?? true }));
                            }}
                        />
                        <Form.Select
                            label="Export As"
                            options={[
                                { key: 'png', text: 'PNG', value: 'png' },
                                { key: 'jpeg', text: 'JPEG', value: 'jpeg' },
                                { key: 'webp', text: 'WEBP', value: 'webp' },
                            ]}
                            value={downloadConfig.exportAs}
                            onChange={(_, data) => setDownloadConfig((prev) => ({ ...prev, exportAs: data.value as ExportFormatValue }))}
                        />
                    </Form.Group>
                </Form>
                <div className="image-preview-container">
                    <Header as="h5" textAlign="center">
                        Preview
                    </Header>
                    {imageUrl ? (
                        <Image
                            src={imageUrl}
                            alt="Chart Preview"
                            fluid
                            style={{
                                backgroundColor: downloadConfig.backgroundColor,
                                border: '1px solid #ddd',
                            }}
                        />
                    ) : (
                        <div
                            style={{
                                padding: '20px',
                                textAlign: 'center',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #ddd',
                            }}
                        >
                            Preview will appear here
                        </div>
                    )}
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleDownloadChart} primary>
                    Download
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

export default DownloadChartModal;
